// manufacturing/ManufacturingStock.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Select from 'react-select';
import PropTypes from 'prop-types';
import {
    Search, RotateCcw, ChevronUp,
    Package, AlertCircle, TrendingUp,
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";

// Pagination component with PropTypes
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
                Page {currentPage} of {totalPages}
            </div>
            <nav>
                <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => onPageChange(currentPage - 1)}>
                            Previous
                        </button>
                    </li>
                    {getPageNumbers().map(pageNum => (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => onPageChange(pageNum)}>
                                {pageNum}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => onPageChange(currentPage + 1)}>
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

Pagination.propTypes = {
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
};

const ManufacturingStock = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [rawMaterialStock, setRawMaterialStock] = useState([]);
    const [finishedGoodsStock, setFinishedGoodsStock] = useState([]);
    const [loadingRaw, setLoadingRaw] = useState(false);
    const [loadingFinished, setLoadingFinished] = useState(false);
    const [activeTab, setActiveTab] = useState('raw_materials');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination states
    const [rawPagination, setRawPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    const [finishedPagination, setFinishedPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        low_stock_only: false,
        sort_by: 'product_name'
    });

    // Load raw material stock
    const loadRawMaterialStock = useCallback(async (page = 1) => {
        try {
            setLoadingRaw(true);
            const params = {
                page,
                limit: rawPagination.limit,
                search: searchTerm || undefined
            };

            const result = await manufacturingActions.getRawMaterialStock(params);
            debugger;
            if (result.success) {
                let data = result.data || [];

                // Apply low stock filter if needed (client-side filter)
                if (filters.low_stock_only) {
                    data = data.filter(item => item.current_stock < (item.alert_quantity || 100));
                }

                // Apply sorting (client-side sort)
                if (filters.sort_by === 'product_name') {
                    data.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));
                } else if (filters.sort_by === 'stock_asc') {
                    data.sort((a, b) => (a.current_stock || 0) - (b.current_stock || 0));
                } else if (filters.sort_by === 'stock_desc') {
                    data.sort((a, b) => (b.current_stock || 0) - (a.current_stock || 0));
                }

                setRawMaterialStock(data);
                setRawPagination(prev => ({
                    ...prev,
                    page: result.page || page,
                    total: result.total || 0,
                    totalPages: result.total_pages || Math.ceil((result.total || 0) / prev.limit)
                }));
            } else {
                toast.error(result.message || 'Failed to load raw material stock');
            }
        } catch (error) {
            console.error('Error loading raw material stock:', error);
            toast.error('Failed to load raw material stock');
        } finally {
            setLoadingRaw(false);
        }
    }, [rawPagination.limit, searchTerm, filters.low_stock_only, filters.sort_by]);

    // Load finished goods stock
    const loadFinishedGoodsStock = useCallback(async (page = 1) => {
        try {
            setLoadingFinished(true);
            const params = {
                page,
                limit: finishedPagination.limit,
                search: searchTerm || undefined
            };

            const result = await manufacturingActions.getFinishedGoodsStock(params);
            if (result.success) {
                let data = result.data || [];

                // Apply low stock filter if needed (client-side filter)
                if (filters.low_stock_only) {
                    data = data.filter(item => (item.manufacturing_stock || 0) < (item.alert_quantity || 500));
                }

                // Apply sorting (client-side sort)
                if (filters.sort_by === 'product_name') {
                    data.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));
                } else if (filters.sort_by === 'stock_asc') {
                    data.sort((a, b) => (a.manufacturing_stock || 0) - (b.manufacturing_stock || 0));
                } else if (filters.sort_by === 'stock_desc') {
                    data.sort((a, b) => (b.manufacturing_stock || 0) - (a.manufacturing_stock || 0));
                }

                setFinishedGoodsStock(data);
                setFinishedPagination(prev => ({
                    ...prev,
                    page: result.page || page,
                    total: result.total || 0,
                    totalPages: result.total_pages || Math.ceil((result.total || 0) / prev.limit)
                }));
            } else {
                toast.error(result.message || 'Failed to load finished goods stock');
            }
        } catch (error) {
            console.error('Error loading finished goods stock:', error);
            toast.error('Failed to load finished goods stock');
        } finally {
            setLoadingFinished(false);
        }
    }, [finishedPagination.limit, searchTerm, filters.low_stock_only, filters.sort_by]);

    // Reset pagination when search or filters change
    useEffect(() => {
        setRawPagination(prev => ({ ...prev, page: 1 }));
        loadRawMaterialStock(1);
        setFinishedPagination(prev => ({ ...prev, page: 1 }));
        loadFinishedGoodsStock(1);
        // Reset to page 1 when search or filters change
        // if (activeTab === 'raw_materials') {
        //     setRawPagination(prev => ({ ...prev, page: 1 }));
        //     loadRawMaterialStock(1);
        // } else {
        //     setFinishedPagination(prev => ({ ...prev, page: 1 }));
        //     loadFinishedGoodsStock(1);
        // }
    }, [searchTerm, filters.low_stock_only, filters.sort_by, activeTab]);

    // Handle page change for raw materials
    const handleRawPageChange = (newPage) => {
        if (newPage >= 1 && newPage <= rawPagination.totalPages) {
            setRawPagination(prev => ({ ...prev, page: newPage }));
            loadRawMaterialStock(newPage);
        }
    };

    // Handle page change for finished goods
    const handleFinishedPageChange = (newPage) => {
        if (newPage >= 1 && newPage <= finishedPagination.totalPages) {
            setFinishedPagination(prev => ({ ...prev, page: newPage }));
            loadFinishedGoodsStock(newPage);
        }
    };

    // Auto-load tab data when switching active tab
    useEffect(() => {
        if (activeTab === 'raw_materials') loadRawMaterialStock(rawPagination.page);
        else loadFinishedGoodsStock(finishedPagination.page);
    }, [activeTab]);

    // Get low stock warning class
    const getStockClass = (quantity, alertQuantity = 100) => {
        if (quantity <= 0) return 'text-danger fw-bold';
        if (quantity < alertQuantity) return 'text-warning fw-bold';
        return 'text-success';
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Calculate totals
    const getTotalStockValue = (stock) => {
        return stock.reduce((sum, item) => sum + ((item.current_stock || item.manufacturing_stock || 0) * (item.cost_price || 0)), 0);
    };

    // Sort options
    const sortOptions = [
        { value: 'product_name', label: 'Product Name' },
        { value: 'stock_asc', label: 'Stock (Low to High)' },
        { value: 'stock_desc', label: 'Stock (High to Low)' }
    ];

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    const isLoading = activeTab === 'raw_materials' ? loadingRaw : loadingFinished;
    const currentStock = activeTab === 'raw_materials' ? rawMaterialStock : finishedGoodsStock;
    const currentPagination = activeTab === 'raw_materials' ? rawPagination : finishedPagination;
    const handlePageChange = activeTab === 'raw_materials' ? handleRawPageChange : handleFinishedPageChange;

    // Refresh handler
    const handleRefresh = () => {
        if (activeTab === 'raw_materials') {
            loadRawMaterialStock(currentPagination.page);
        } else {
            loadFinishedGoodsStock(currentPagination.page);
        }
        toast.info('Refreshing stock data...');
    };

    // Clear filters handler
    const handleClearFilters = () => {
        setSearchTerm('');
        setFilters({ low_stock_only: false, sort_by: 'product_name' });
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Manufacturing Stock</h4>
                        <h6>View raw materials and finished goods stock at manufacturing unit</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={handleRefresh}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Stock Summary Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                                        <Package size={24} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Raw Material Items</p>
                                        <h3 className="mb-0">{rawPagination.total}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                        <TrendingUp size={24} className="text-success" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Finished Goods Items</p>
                                        <h3 className="mb-0">{finishedPagination.total}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-info bg-opacity-10 p-3 rounded me-3">
                                        <Package size={24} className="text-info" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Raw Material Value</p>
                                        <h3 className="mb-0">{formatCurrency(getTotalStockValue(rawMaterialStock))}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                                        <AlertCircle size={24} className="text-warning" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Finished Goods Value</p>
                                        <h3 className="mb-0">{formatCurrency(getTotalStockValue(finishedGoodsStock))}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-4">
                                <div className="mb-3">
                                    <label>Search</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Product name, SKU..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button className="btn btn-primary">
                                            <Search size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Sort By</label>
                                    <Select
                                        options={sortOptions}
                                        value={sortOptions.find(opt => opt.value === filters.sort_by)}
                                        onChange={(selected) => setFilters(prev => ({ ...prev, sort_by: selected?.value || 'product_name' }))}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="lowStockOnly"
                                            checked={filters.low_stock_only}
                                            onChange={(e) => setFilters(prev => ({ ...prev, low_stock_only: e.target.checked }))}
                                        />
                                        <label className="form-check-label" htmlFor="lowStockOnly">
                                            Show Low Stock Only
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <button className="btn btn-secondary w-100" onClick={handleClearFilters}>
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'raw_materials' ? 'active' : ''}`}
                            onClick={() => setActiveTab('raw_materials')}
                        >
                            <Package size={16} className="me-2" />
                            Raw Materials ({rawPagination.total})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'finished_goods' ? 'active' : ''}`}
                            onClick={() => setActiveTab('finished_goods')}
                        >
                            <TrendingUp size={16} className="me-2" />
                            Finished Goods ({finishedPagination.total})
                        </button>
                    </li>
                </ul>

                {/* Stock Table */}
                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : currentStock.length === 0 ? (
                            <div className="text-center py-5">
                                {activeTab === 'raw_materials' ? (
                                    <>
                                        <Package size={48} className="text-muted mb-3" />
                                        <h5>No raw materials found</h5>
                                        <p className="text-muted">Raw materials will appear here after dispatch and receive</p>
                                    </>
                                ) : (
                                    <>
                                        <TrendingUp size={48} className="text-muted mb-3" />
                                        <h5>No finished goods found</h5>
                                        <p className="text-muted">Finished goods will appear after production entry</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Product Name</th>
                                                <th>SKU</th>
                                                <th>Unit</th>
                                                <th>Current Stock</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentStock.map((item) => (
                                                <tr key={item.product_id}>
                                                    <td>
                                                        <strong>{item.product_name}</strong>
                                                        {item.cost_price && (
                                                            <small className="d-block text-muted">
                                                                Cost: {formatCurrency(item.cost_price)}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td><code>{item.sku}</code></td>
                                                    <td>{item.unit_name}</td>
                                                    <td className={getStockClass(
                                                        activeTab === 'raw_materials' ? item.current_stock : item.manufacturing_stock,
                                                        activeTab === 'raw_materials' ? 100 : 500
                                                    )}>
                                                        {activeTab === 'raw_materials'
                                                            ? (item.current_stock?.toLocaleString() || 0)
                                                            : (item.manufacturing_stock?.toLocaleString() || 0)
                                                        }
                                                        {activeTab === 'finished_goods' && item.total_produced && (
                                                            <small className="d-block text-muted">
                                                                Produced: {item.total_produced?.toLocaleString() || 0} |
                                                                Transferred: {item.total_transferred?.toLocaleString() || 0}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const stock = activeTab === 'raw_materials' ? item.current_stock : item.manufacturing_stock;
                                                            const alertQty = activeTab === 'raw_materials' ? 100 : 500;
                                                            if (stock <= 0) return <span className="badge bg-danger">Out of Stock</span>;
                                                            if (stock < alertQty) return <span className="badge bg-warning">Low Stock</span>;
                                                            return <span className="badge bg-success">In Stock</span>;
                                                        })()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination
                                    currentPage={currentPagination.page}
                                    totalPages={currentPagination.totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManufacturingStock;